import { Component, ChangeDetectionStrategy, ChangeDetectorRef, OnInit, OnDestroy } from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { FormInputViewerService } from '../../form-input-viewer.service';
import { AssistantMetaService, IAssistantAreaMeta, IAssistantMeta } from 'app/main/components/assistant/assistant-meta.service';

type AreaGroup = IAssistantMeta['groups'][number];

/**
 * Switch per area dell'assistente AI raggruppati per tipologia (form "Assistente AI" del dominio). Le aree arrivano
 * dal server (assistant/meta): per ognuna un controllo con la sua chiave piatta (assente = attiva); il controllo
 * segnaposto del campo viene tolto, così si salvano solo le aree. Nuova area sul server = nuovo switch, niente codice qui.
 */
@Component({
	selector: 'assistant-areas-input',
	templateUrl: './input.component.html',
	styleUrls: ['./input.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false
})
export class AssistantAreasInputComponent implements OnInit, OnDestroy{

	groups: AreaGroup[] = [];
	private destroyed = new Subject<void>();

	constructor(
		public inputService: FormInputViewerService,
		private metaService: AssistantMetaService,
		private changeDetector: ChangeDetectorRef
	){}

	ngOnInit(): void{
		this.metaService.load();
		this.metaService.meta
		.pipe(filter(Boolean), takeUntil(this.destroyed))
		.subscribe(meta => {
			this.bindControls(meta);
			this.groups = meta.groups;
			this.changeDetector.markForCheck();
		});
	}

	ngOnDestroy(): void{
		this.destroyed.next();
		this.destroyed.complete();
	}

	activeCount(group: AreaGroup): number{
		return group.areas.filter(area => this.control(area)?.value !== false).length;
	}

	/** select "Assistente AI" del form: spento → gruppi attenuati (gli switch restano modificabili) */
	get moduleEnabled(): boolean{
		const enabled = this.inputService.formGroup?.get('enabled')?.value;
		return enabled === true || enabled === 'true' || enabled === 1;
	}

	control(area: IAssistantAreaMeta): AbstractControl | null{
		return this.inputService.formGroup?.get(area.key) ?? null;
	}

	private bindControls(meta: IAssistantMeta): void{
		const formGroup = this.inputService.formGroup;
		if(!formGroup) return;
		const record = this.inputService.data || {};
		for(const area of meta.groups.flatMap(group => group.areas)){
			if(formGroup.get(area.key)) continue;
			const saved = record[area.key];
			const off = saved === false || saved === 'false' || saved === 0 || saved === '0';
			formGroup.addControl(area.key, new FormControl(!off));
		}
		if(this.inputService.formControlKey && formGroup.get(this.inputService.formControlKey)){
			formGroup.removeControl(this.inputService.formControlKey);
		}
	}
}
